import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import type {
  EmailDomainsResponse,
  DnsCheckResponse,
  EmailAuthSummaryResponse,
  DmarcReportsResponse,
  CreateEmailDomainRequest,
  UploadDmarcReportRequest,
  UploadDmarcReportResponse,
  EmailDomain,
} from "@/types/email-auth";

// -- Domains --------------------------------------------------------

export function useEmailDomains() {
  return useQuery<EmailDomainsResponse>({
    queryKey: ["email-auth", "domains"],
    queryFn: () => apiGet<EmailDomainsResponse>("/api/v1/email-auth/domains"),
  });
}

export function useAddEmailDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEmailDomainRequest) =>
      apiPost<{ domain: EmailDomain }>("/api/v1/email-auth/domains", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-auth"] });
    },
  });
}

export function useDeleteEmailDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) =>
      apiDelete<{ status: string }>(`/api/v1/email-auth/domains/${domainId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-auth"] });
    },
  });
}

export function useCheckDomainDns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) =>
      apiPost<DnsCheckResponse>(`/api/v1/email-auth/domains/${domainId}/check`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-auth"] });
    },
  });
}

// -- Summary --------------------------------------------------------

export function useEmailAuthSummary() {
  return useQuery<EmailAuthSummaryResponse>({
    queryKey: ["email-auth", "summary"],
    queryFn: () => apiGet<EmailAuthSummaryResponse>("/api/v1/email-auth/summary"),
    refetchInterval: 60_000,
  });
}

// -- DMARC Reports --------------------------------------------------

export function useDmarcReports(params?: { domain?: string; limit?: number; offset?: number }) {
  return useQuery<DmarcReportsResponse>({
    queryKey: ["email-auth", "reports", params],
    queryFn: () => {
      const qs: Record<string, string> = {};
      if (params?.domain) qs.domain = params.domain;
      if (params?.limit) qs.limit = String(params.limit);
      if (params?.offset) qs.offset = String(params.offset);
      return apiGet<DmarcReportsResponse>("/api/v1/email-auth/reports", qs);
    },
  });
}

export function useUploadDmarcReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UploadDmarcReportRequest) =>
      apiPost<UploadDmarcReportResponse>("/api/v1/email-auth/reports", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-auth", "reports"] });
      qc.invalidateQueries({ queryKey: ["email-auth", "summary"] });
    },
  });
}
